<template>
  <UiComponentWrapper class="page no-ls">
    <!-- Section > Hero -->
    <UiComponentWrapper class="jumbotron">
      <UiComponentContainer>
        <OrganismHero>
          <template #tag>
            <NuxtLink :to="localePath('software-comparison')">
              {{ heroTag }}
            </NuxtLink>
          </template>
          <template #heading>
            {{ heroTitle }}
          </template>
          <template #paragraph>
            {{ heroText }}
          </template>
        </OrganismHero>
      </UiComponentContainer>
      <UiComponentImage
        :key="screenWidth"
        class="pattern horizontal"
        :height="props.hub.heroPatternDesktop.height"
        :expected-size="screenWidth"
        :lazyload="false"
        :source="props.hub.heroPatternDesktop.url"
        :width="props.hub.heroPatternDesktop.width"
      />
      <UiComponentImage
        :key="screenWidth"
        class="pattern vertical"
        :expected-size="screenWidth"
        :height="props.hub.heroPatternMobile.height"
        :lazyload="false"
        orientation="portrait"
        :source="props.hub.heroPatternMobile.url"
        :width="props.hub.heroPatternMobile.width"
      />
    </UiComponentWrapper>

    <!-- Section > Table -->
    <UiComponentWrapper class="comparison-table">
      <UiComponentWrapper
        class="header-fixed"
        :class="{ active: displayFixedHeaderTable }"
      >
        <UiComponentContainer class="">
          <UiComponentWrapper>
            <UiComponentParagraph>{{ activeCategory }}</UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper>
            <UiComponentParagraph :style="{ color: props.page.company1.companyColor.hex }">
              {{
                props.page.company1.companyName
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper>
            <UiComponentParagraph :style="{ color: props.page.company2.companyColor.hex }">
              {{
                props.page.company2.companyName
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentContainer>
      </UiComponentWrapper>
      <UiComponentWrapper class="table">
        <OrganismTableCompare
          ref="$table"
          :features="allFeaturesTable"
          :features-categories="props.allFeaturesCategories"
          :first-software="props.page.company1"
          :is-livestorm="false"
          :second-software="props.page.company2"
          :rating-date="oldHumanReadableDate"
        />
      </UiComponentWrapper>
    </UiComponentWrapper>
    <UiComponentContainer
      :start="1"
      :end="1"
      class="last-update"
    >
      <UiComponentParagraph size="s">
        {{ recentHumanReadableDate }} -
        <AtomButton
          type="text"
          :to="lastUpdateButton.to"
        >
          {{
            lastUpdateButton.slot
          }}
        </AtomButton>
      </UiComponentParagraph>
    </UiComponentContainer>

    <!-- Section > Choose Livestorm -->
    <UiComponentContainer>
      <MoleculeCallToAction
        :call-to-action="{
          ...shortCtaButton,
          label: 'Small section CTA',
        }"
        :pattern-desktop="props.componentContentAVsB.getStartedImageBackground"
        :pattern-mobile="props.componentContentAVsB.getStartedImageMobile"
      >
        <template #heading>
          {{ props.componentContentAVsB.getStartedTitle }}
        </template>
        <template #paragraph>
          {{ props.componentContentAVsB.getStartedText }}
        </template>
      </MoleculeCallToAction>
    </UiComponentContainer>

    <!-- Section > What Is -->
    <UiComponentContainer class="what-is">
      <UiComponentWrapper class="company">
        <UiComponentWrapper>
          <UiComponentHeading :level="2">
            {{
              $interpolate(props.componentContentAVsB.whatIsTitle1, {
                "%{company1}": props.page.company1.companyName,
              })
            }}
          </UiComponentHeading>
          <OrganismRichText
            :content="[
              {
                __typename: 'ContentTextHtmlRecord',
                text: props.page.whatIsText1 || '',
              },
            ]"
          />
        </UiComponentWrapper>
        <UiComponentWrapper class="badges">
          <UiComponentHeading :level="3">
            {{
              $interpolate(props.componentContentAVsB.bestForTitle1, {
                "%{company1}": props.page.company1.companyName,
              })
            }}
          </UiComponentHeading>
          <UiComponentWrapper>
            <UiComponentWrapper
              v-for="badge in props.page.company1.badgesBestFor"
              :key="badge.id"
              class="badge"
            >
              <UiComponentImage
                :alt="badge.image.alt"
                :expected-size="badgeImageSize"
                :height="badge.image.height"
                :source="badge.image.url"
                :width="badge.image.width"
              />
              <UiComponentParagraph :level="3">
                {{ badge.title }}
              </UiComponentParagraph>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="company">
        <UiComponentWrapper>
          <UiComponentHeading :level="2">
            {{
              $interpolate(props.componentContentAVsB.whatIsTitle2, {
                "%{company2}": props.page.company2.companyName,
              })
            }}
          </UiComponentHeading>
          <OrganismRichText
            :content="[
              {
                __typename: 'ContentTextHtmlRecord',
                text: props.page.whatIsText2 || '',
              },
            ]"
          />
        </UiComponentWrapper>
        <UiComponentWrapper class="badges">
          <UiComponentHeading :level="3">
            {{
              $interpolate(props.componentContentAVsB.bestForTitle2, {
                "%{company2}": props.page.company2.companyName,
              })
            }}
          </UiComponentHeading>
          <UiComponentWrapper>
            <UiComponentWrapper
              v-for="badge in props.page.company2.badgesBestFor"
              :key="badge.id"
              class="badge"
            >
              <UiComponentImage
                :alt="badge.image.alt"
                :expected-size="badgeImageSize"
                :height="badge.image.height"
                :source="badge.image.url"
                :width="badge.image.width"
              />
              <UiComponentParagraph :level="3">
                {{ badge.title }}
              </UiComponentParagraph>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Tabs -->
    <UiComponentContainer
      class="tabs-with-image large"
      :start="0"
      :end="0"
    >
      <UiComponentHeading :level="2">
        {{ tabsTitle }}
      </UiComponentHeading>
      <UiComponentWrapper class="items">
        <UiComponentWrapper
          v-for="({ id, subTitle: title, paragraph }, index) in tabs"
          :key="id"
          class="item"
        >
          <UiComponentWrapper class="content">
            <UiComponentHeading :level="3">
              {{ title }}
            </UiComponentHeading>
            <div
              class="description"
              v-html="paragraph"
            />
            <UiComponentWrapper
              v-if="index === 0"
              class="actions"
            >
              <AtomButton
                :action="signupOrUpgradeCallToAction.action"
                label="Comparison section CTA"
                size="l"
                :to="signupOrUpgradeCallToAction.to"
              >
                {{ signupOrUpgradeCallToAction.slot }}
              </AtomButton>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Best Webinar Software -->
    <UiComponentWrapper
      class="livestorm-best-software"
      :start="0"
      :end="0"
    >
      <UiComponentContainer>
        <OrganismTabsWithImage
          :tabs="tabsLivestormSoftware"
          :title="props.componentContentLivestorm.livestormPromotionTitle"
          :primary-button="{
            action: signupOrUpgradeCallToAction.action,
            label: 'Livestorm promotion section CTA',
            slot: signupOrUpgradeCallToAction.slot,
            to: signupOrUpgradeCallToAction.to,
          }"
          :secondary-button="{
            action: requestDemoCallToAction.action,
            label: 'Livestorm promotion section CTA',
            slot: requestDemoCallToAction.slot,
            to: requestDemoCallToAction.to,
          }"
        />
      </UiComponentContainer>
      <UiComponentImage
        :alt="props.componentContentLivestorm.livestormPromotionBackgroundDesktop.alt"
        class="pattern"
        :expected-size="livestormBestSoftwareSectionWidth"
        :height="livestormBestSoftwareSectionHeight"
        :orientation="
          livestormBestSoftwareSectionWidth
            / livestormBestSoftwareSectionHeight
            >= 1
            ? 'landscape'
            : 'portrait'
        "
        :source="props.componentContentLivestorm.livestormPromotionBackgroundDesktop.url"
        :width="livestormBestSoftwareSectionWidth"
      />
    </UiComponentWrapper>

    <!-- Section > Testimonials -->
    <UiComponentWrapper class="testimonials">
      <div class="glide">
        <div
          class="glide__track"
          data-glide-el="track"
        >
          <div class="flex glide__slides">
            <MoleculeTestimonialCard
              v-for="(testimonial, index) in testimonials"
              :key="index"
              class="h-auto glide__slide"
              :testimonial="testimonial"
            >
              {{ props.componentGlobal.readTheCustomerStory }}
            </MoleculeTestimonialCard>
          </div>
        </div>
      </div>
      <UiComponentContainer class="actions">
        <UiComponentWrapper class="navigation">
          <UiComponentIcon
            type="arrow-left"
            @click="glide.go('<')"
          />
          <UiComponentIcon
            type="arrow-right"
            @click="glide.go('>')"
          />
        </UiComponentWrapper>
        <AtomButton
          type="text"
          :to="localePath('customers')"
        >
          {{ props.componentGlobal.readTheirStories }}
          <UiComponentIcon type="arrow-right" />
        </AtomButton>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Related Comparisons -->
    <UiComponentContainer class="related-comparisons">
      <UiComponentHeading :level="2">
        {{ relatedComparisonTitle }}
      </UiComponentHeading>
      <UiComponentWrapper>
        <MoleculeComparisonCard
          v-for="comparedPage in props.page.relatedPages"
          :key="comparedPage.id"
          :first-software="{
            id: comparedPage.company1.id,
            name: comparedPage.company1.companyName,
            color: comparedPage.company1.companyColor.hex,
          }"
          :second-software="{
            id: comparedPage.company2.id,
            name: comparedPage.company2.companyName,
            color: comparedPage.company2.companyColor.hex,
          }"
          :slug="comparedPage.slug"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Get Started -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
import Glide from '@glidejs/glide'
import dayjs from 'dayjs'

// Props
const props = defineProps({
  allFeaturesCategories: {
    required: true,
    type: Array,
  },
  allFeaturesComparisons: {
    required: true,
    type: Array,
  },
  componentContentAVsB: {
    required: true,
    type: Object,
  },
  componentContentLivestorm: {
    required: true,
    type: Object,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  home: {
    required: true,
    type: Object,
  },
  hub: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
})

// Composables
const { page } = useAnalyticsJS()
const { updateListItems } = useBreadcrumb()
const { camelCase } = useCamelCase()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $interpolate } = useNuxtApp()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Refs
const $table = ref(null)
const activeCategory = ref('')
const displayFixedHeaderTable = ref(false)
const glide = ref(false)
const livestormBestSoftwareSectionHeight = ref(600)
const livestormBestSoftwareSectionWidth = ref(1440)
const oldHumanReadableDate = ref('')
const recentHumanReadableDate = ref('')
const requestAnimationFrameId = ref(null)

// Computed Properties
const allFeaturesTable = computed(() => {
  return props.allFeaturesComparisons.map(feature => ({
    ...feature,
    key: camelCase(feature.slug),
  }))
})

const badgeImageSize = computed(() => {
  return screenWidth.value < 768 ? 80 : 164
})

const heroTag = computed(() => {
  return props.componentContentAVsB.heroTag
})

const heroTitle = computed(() => {
  return $interpolate(props.componentContentAVsB.heroTitle, {
    '%{company1}': props.page.company1.companyName,
    '%{company2}': props.page.company2.companyName,
  })
})

const heroText = computed(() => {
  return $interpolate(props.componentContentAVsB.heroText, {
    '%{company1}': props.page.company1.companyName,
    '%{company2}': props.page.company2.companyName,
  })
})

const lastUpdateButton = computed(() => {
  return {
    to: `mailto:${props.hub.askToEditMailTo}`,
    slot: props.hub.askToEdit,
  }
})

const livestormFeatures = computed(() => {
  return [
    props.componentGlobal.noCreditCard,
    props.componentGlobal.noInstall,
  ]
})

const relatedComparisonTitle = computed(() => {
  return props.componentContentAVsB.relatedTitle
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const shortCtaButton = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaTryForFreeShort,
      }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaGetStartedForFree,
      }
})

const tabs = computed(() => {
  return props.componentContentAVsB.tabTitleSubtitle.map((tab, index) => ({
    id: tab.id,
    tabTitle: tab.tabTabTitle,
    subTitle: $interpolate(tab.tabSubtitle, {
      '%{company1}': props.page.company1.companyName,
      '%{company2}': props.page.company2.companyName,
    }),
    paragraph: $md.render(props.page.aVsBTextTab[index]?.content),
  }))
})

const tabsLivestormSoftware = computed(() => {
  return props.componentContentLivestorm.tabs.map(tab => ({
    id: tab.id,
    image: tab.asset,
    tabTitle: tab.tab,
    subTitle: tab.subTitle,
    paragraph: tab.paragraph,
  }))
})

const tabsTitle = computed(() => {
  return $interpolate(props.componentContentAVsB.textTabTitle, {
    '%{company1}': props.page.company1.companyName,
    '%{company2}': props.page.company2.companyName,
  })
})

const testimonials = computed(() => {
  return props.componentContentLivestorm.customersTestimonials
    .filter(customer => customer.authorPicture && customer.logoColor)
    .map(customer => ({
      id: customer.id,
      customer: {
        avatar: {
          alt: `${customer.authorUserStory}' Avatar`,
          height: customer.authorPicture.height,
          src: customer.authorPicture.url,
          width: customer.authorPicture.width,
        },
        name: customer.authorUserStory,
        position: customer.authorPosition,
      },
      logo: {
        alt: `${customer.name}' logo`,
        height: customer.logoColor.height,
        src: customer.logoColor.url,
        width: customer.logoColor.width,
      },
      quote: customer.quote,
      slug:
        customer.contentUserStory.length > 0
          ? localePath({
              name: 'customers-slug',
              params: { slug: customer.slug },
            })
          : undefined,
    }))
})

// Methods
function formatDate(date) {
  return `${props.hub.lastUpdate} ${dayjs(date).format(
    'MMMM D, YYYY',
  )}`
}

function onRequestAnimationFrame() {
  const { $el, getActiveCategory } = $table.value
  const { offsetHeight, offsetTop } = $el
  const { innerWidth, scrollY } = window
  const margin = innerWidth < 1328 ? 16 : 64
  const offsetNavBar = innerWidth < 1328 ? 80 : 160
  activeCategory.value = getActiveCategory(scrollY, offsetNavBar)

  if (scrollY >= offsetTop + margin && scrollY < offsetTop + offsetHeight) {
    displayFixedHeaderTable.value = true
  }
  else {
    displayFixedHeaderTable.value = false
  }

  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )
}

function onObserve() {
  const livestormBestSoftwareSection = document.querySelector(
    '.page .livestorm-best-software',
  )
  livestormBestSoftwareSectionHeight.value
    = livestormBestSoftwareSection.offsetHeight
  livestormBestSoftwareSectionWidth.value
    = livestormBestSoftwareSection.offsetWidth
}

function sortCompaniesByUpdateDate(companies, orderBy) {
  switch (orderBy) {
    case 'DESC':
      return companies.sort(
        (a, b) => new Date(b._updatedAt) - new Date(a._updatedAt),
      )
    case 'ASC':
      return companies.sort(
        (a, b) => new Date(a._updatedAt) - new Date(b._updatedAt),
      )
    default:
      return companies
  }
}

// Lifecycle hooks
onBeforeUnmount(() => {
  window.cancelAnimationFrame(requestAnimationFrameId.value)
  window.removeEventListener('resize', onObserve)

  glide.value?.destroy()
})

onMounted(() => {
  window.addEventListener('resize', onObserve)

  page(`${props.page.company1?.companyName}-vs-${props.page.company2?.companyName}`)
  updateListItems([
    {
      item: localePath('index'),
      name: props.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('software-comparison'),
      name: props.hub.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: `${props.page.company1.companyName} vs ${props.page.company2.companyName}`,
    },
  ])

  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )
  const companies = [props.page.company1, props.page.company2]

  // Get recent company
  const [recentCompany] = sortCompaniesByUpdateDate(companies, 'DESC')
  recentHumanReadableDate.value = formatDate(recentCompany._updatedAt)

  // Get older company
  const [olderCompany] = sortCompaniesByUpdateDate(companies, 'ASC')
  oldHumanReadableDate.value = formatDate(olderCompany._updatedAt)

  glide.value = new Glide('.testimonials .glide', {
    type: 'carousel',
    focusAt: 'center',
    gap: 40,
    peek: {
      before: 40,
      after: 40,
    },
    perView: 5,
    breakpoints: {
      1920: {
        peek: {
          before: 40,
          after: 40,
        },
        perView: 4,
      },
      1680: {
        peek: {
          before: 40,
          after: 40,
        },
        perView: 3,
      },
      1024: {
        peek: {
          before: 24,
          after: 24,
        },
        perView: 2,
      },
      768: {
        peek: {
          before: 16,
          after: 16,
        },
        perView: 1,
      },
    },
  })
  glide.value.mount()

  nextTick(() => {
    onObserve()
  })
})
</script>

<style lang="postcss" scoped>
@import "../../../assets/css/glide.css";

.page {
  @apply flex-col justify-center items-center;

  .jumbotron {
    @apply flex-col items-center justify-center;

    .container {
      @apply justify-center;

      .hero {
        @apply pt-8;
        max-width: 800px;

        @screen md {
          @apply pt-16;
        }

        @screen lg {
          @apply pt-20;
        }

        @screen xxxl {
          @apply pt-26;
        }

        :deep(.button-list) {
          .button {
            &:last-child {
              @apply bg-winter-green-900 text-white;
            }
          }
        }
      }
    }

    .pattern {
      &.horizontal,
      &.vertical {
        @apply absolute top-0 left-0 right-0 w-full h-full hidden rounded-none;
        z-index: -1;
      }
    }
  }

  .comparison-table {
    @apply mt-20 flex-col items-center overflow-x-scroll overflow-y-hidden;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34 overflow-visible;
    }

    .header-fixed {
      @apply fixed h-16 z-10 justify-center bg-white shadow-light2 invisible opacity-0 transition-opacity duration-300 ease-in-out;
      top: 56px;
      will-change: opacity;

      @screen xl {
        top: 72px;
      }

      &.active {
        @apply visible opacity-100;
      }

      .container {
        @apply p-0 flex-row items-center;
        width: 408px;
        max-width: 1060px;

        @screen md {
          @apply px-6 w-full;
        }

        @screen xl {
          @apply px-0;
        }

        .wrapper {
          @apply relative flex-shrink-0 h-full justify-center items-center;
          width: 119px;

          @screen md {
            width: 214px;
          }

          @screen xl {
            width: 330px;
          }

          &:before {
            @apply absolute left-0 h-full border-l-2 border-grey-blue-200;
            content: "";
            top: 0;
          }

          &:first-child {
            @apply w-full flex-shrink;

            &:before {
              @apply hidden;
            }

            p {
              @apply pl-4 text-left text-winter-green-900;

              @screen md {
                @apply pl-8;
              }
            }
          }

          &:last-child {
            &:after {
              content: "";
              top: 0;
              @apply absolute h-full right-0 border-l-2 border-grey-blue-200;
            }
          }

          &.livestorm {
            @apply px-4;
            width: 122px;

            @screen md {
              @apply px-0;
              width: 214px;
            }

            @screen xl {
              width: 370px;
            }
          }

          p {
            @apply mt-0 w-full text-sm font-medium text-center;

            @screen md {
              @apply text-lg;
            }
          }
        }
      }
    }

    .table {
      @apply flex-col;

      .table-compare {
        @apply mx-auto;
      }
    }
  }

  .last-update {
    @apply justify-center;

    @screen md {
      @apply justify-end;
    }

    p {
      @apply mt-8 flex flex-col items-center self-center text-grey-blue-600;

      @screen md {
        @apply flex-row self-end;
      }

      .button {
        @apply ml-1 p-0 font-normal text-sm text-grey-blue-600 transition-colors duration-500 ease-in-out;

        &:hover {
          @apply text-livestorm-blue-600;
        }
      }
    }
  }

  .small-call-to-action {
    max-width: 792px;
    @apply mt-14 mx-auto;

    @screen mdminus {
      @apply mt-16;
    }

    @screen mdplus {
      @apply mt-26;
    }

    :deep(h4) {
      @apply font-medium;
    }
  }

  .what-is {
    @apply mt-20 flex-col;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }

    .company {
      @apply mt-14 flex flex-col w-full;

      @screen mdminus {
        @apply mt-20;
      }

      @screen mdplus {
        @apply mt-34;
      }

      @screen xl {
        @apply flex-row justify-between;
      }

      > .wrapper {
        @apply flex-col;

        @screen xl {
          width: 620px;
        }
      }

      &:first-child {
        @apply mt-0;
      }

      .rich-text {
        @apply mt-4 text-grey-blue-600;

        :deep(p) {
          @apply text-grey-blue-600;
        }
      }

      .badges {
        @apply mt-8 flex-col;

        @screen md {
          @apply mt-10;
        }

        @screen xl {
          @apply mt-0;
          width: 620px;
        }

        .h3 {
          @apply font-medium text-grey-blue-600 text-origin leading-7 !important;
        }

        > .wrapper {
          @apply flex-col;

          @screen md {
            @apply flex-row mt-8;
          }

          .badge {
            @apply mt-8 border-2 border-grey-blue-200 rounded-[24px] items-center;
            padding: 6px;

            @screen md {
              @apply ml-10 mt-0 px-2 pb-3 flex-col;
              border-radius: 2.2rem;
              padding-top: 6px;
              width: 180px;
            }

            &:first-child {
              @screen md {
                @apply ml-0;
              }
            }

            picture {
              @apply shadow-light4;
              border-radius: 16px;

              @screen md {
                border-radius: 1.65rem;
              }
            }

            p {
              @apply ml-6 text-sm font-medium text-grey-blue-600 leading-6;

              @screen md {
                @apply flex mt-2 ml-0 h-12 text-center items-center;
                max-width: 148px;
              }
            }
          }
        }
      }
    }
  }

  .container {
    &.tabs-with-image {
      @apply flex-col mt-26;

      @screen mdminus {
        @apply mt-26;
      }

      @screen mdplus {
        @apply mt-46;
      }

      &.large {
        .h2 {
          @screen lg {
            max-width: 74.21875%;
          }

          :deep(strong) {
            @apply text-winter-green-600;
          }
        }

        .items {
          @apply flex-col mt-12;

          @screen lg {
            @apply w-span9;
          }

          .item {
            @apply flex-col mt-12;

            .content {
              @apply flex-col;

              @screen lg {
                @apply w-span12;
              }
            }

            .image {
              @apply hidden;
            }
          }
        }
      }

      .h2 {
        @screen lg {
          max-width: 48.4375%;
        }
      }

      .items {
        @apply flex-col mt-12;

        .item {
          @apply flex-col mt-26;

          @screen mdminus {
            @apply mt-26;
          }

          @screen mdplus {
            @apply mt-46;
          }

          @screen lg {
            @apply flex-row flex-wrap items-center justify-between;
          }

          &:first-child {
            @apply mt-0;
          }

          :deep(.content) {
            @apply flex-col;

            @screen lg {
              @apply w-span6;
            }

            .title {
              @apply font-medium text-2xl text-winter-green-900 leading-8;
            }

            .h3 {
              :deep(strong) {
                @apply text-winter-green-600;
              }
            }

            .description {
              @apply text-grey-blue-600 text-lg leading-7 whitespace-pre-wrap;
            }

            .title + .description,
            .h3 + .description {
              @apply text-grey-blue-600 text-origin leading-8;

              a {
                @apply text-livestorm-blue-700;
              }
            }

            .title + .description {
              @apply mt-4;
            }

            .h3 + .description {
              @apply mt-8;
            }

            .actions {
              @apply flex-col mt-8;

              @screen md {
                @apply flex-row;
              }

              .button + .button {
                @apply mt-4;

                @screen md {
                  @apply mt-0 ml-6;
                }
              }
            }
          }

          .image {
            @apply mt-16;

            @screen lg {
              @apply w-span5 mt-0;
            }

            .inner {
              @apply flex-col;

              @screen lg {
                @apply w-auto;
              }
            }

            picture {
              @apply rounded-none;

              &.add-shadow {
                @apply shadow-light6;
                border-radius: 8px;

                @screen lg {
                  @apply rounded-[24px];
                }
              }
            }
          }
        }
      }
    }
  }

  .livestorm-best-software {
    @apply mt-26 relative justify-center overflow-hidden;

    @screen mdminus {
      @apply mt-26;
    }

    @screen mdplus {
      @apply mt-46;
    }

    .pattern {
      @apply absolute inset-0 w-full h-full rounded-none;
      z-index: -1;
    }

    .container {
      @apply py-18;

      @screen md {
        @apply py-26;
      }

      :deep(.title) {
        strong {
          @apply text-winter-green-600;
        }
      }

      :deep(.title-tab) {
        strong {
          @apply text-winter-green-600 font-medium;
        }
      }

      :deep(.rich-text) {
        p {
          @apply text-grey-blue-800;
        }
      }
    }

    :deep(.button-list) {
      .button {
        &:last-child {
          @apply bg-winter-green-900 text-white;
        }
      }
    }
  }

  .testimonials {
    @apply mt-26 flex-col items-center;

    @screen mdminus {
      @apply mt-26;
    }

    @screen mdplus {
      @apply mt-46;
    }

    .slider {
      @apply h-auto;

      .inner {
        @apply h-auto;

        .testimonial-card {
          @apply w-85;

          @screen xl {
            @apply w-100;
          }
        }
      }
    }

    .actions {
      @apply justify-between items-center mt-12;

      .navigation {
        i {
          @apply mx-2 cursor-pointer;

          &:first-child {
            @apply ml-0;
          }

          &:last-child {
            @apply mr-0;
          }
        }
      }

      .button {
        @apply flex-shrink-0 items-center;

        i {
          @apply ml-2;
        }
      }
    }
  }

  .related-comparisons {
    @apply mt-26 flex-col justify-center items-center;

    @screen mdminus {
      @apply mt-26;
    }

    @screen mdplus {
      @apply mt-46;
    }

    h2 {
      @apply text-center;

      @screen md {
        @apply text-left;
      }

      :deep(strong) {
        @apply text-sunburst-red-400;
      }
    }

    .wrapper {
      @apply mt-14 flex-wrap;

      @screen mdminus {
        @apply mt-16;
      }

      @screen mdplus {
        @apply mt-26;
      }

      .comparison-livestorm-card,
      .comparison-card {
        @apply mt-4 w-full;

        @screen md {
          width: calc(50% - 20px);
          @apply mt-10 ml-10;
        }

        @screen xl {
          width: 290px;
        }

        &:first-child {
          @apply mt-0 ml-0;
        }

        &:nth-child(2n + 1) {
          @screen md {
            @apply ml-0;
          }

          @screen xl {
            @apply ml-10;
          }
        }

        &:nth-child(4n + 1) {
          @screen xl {
            @apply ml-0;
          }
        }

        &:nth-child(2) {
          @screen md {
            @apply mt-0;
          }
        }

        &:nth-child(3) {
          @screen xl {
            @apply mt-0;
          }
        }

        &:nth-child(4) {
          @screen xl {
            @apply mt-0;
          }
        }
      }
    }
  }

  &.no-ls {
    .container.tabs-with-image {
      :deep(.container) {
        @apply w-full;

        .title {
          max-width: 1000px;

          strong {
            @apply text-winter-green-600;
          }
        }

        .title-tab {
          strong {
            @apply text-winter-green-600 font-medium;
          }
        }

        .button {
          @apply ml-0;
        }
      }

      :deep(.media-right) {
        @apply hidden;
      }
    }
  }

  .get-started {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }
  }
}

@media (orientation: landscape) {
  .page .jumbotron .pattern.horizontal {
    @apply flex !important;
  }
}

@media (orientation: portrait) {
  .page .jumbotron .pattern.vertical {
    @apply flex !important;
  }
}
</style>
